import classes from './SmallCardsGrid.module.scss';
import SmallCard from 'Components/SmallCard/SmallCard';

import { useNavigate } from 'react-router-dom';
import { useGlobalState } from 'Providers/GlobalStateProvider';
import { useUser } from 'Providers/UserProvider';
import { statsQuerySerialiser } from '~/features/stats-details/query-parsers';
import { getAvailabilityColor } from 'Utils/getAvailabilityColor';

interface ILiftStatsAvailability {
  count?: number;
  lift_count?: number;
  unavailable?: number;
  available?: number;
  total?: number;
  percentage: number;
  bracket?: Array<unknown>;
}

interface ILiftStatsCount {
  count: number;
  lift_count?: number;
  lifts?: string[];
  events?: string[];
  bracket?: Array<unknown>;
}

interface ILiftStats {
  liftUnavailable?: number;
  intercomUnavailable?: number;
  senseFailureSensor?: number;
  visits?: number;
  senseNotConnected?: number;
  servicecounterMaintenanceAlert?: number;
  approvalRejected?: number | ILiftStatsCount;
  approvalExpired?: number | ILiftStatsCount;
  liftAvailability?: ILiftStatsAvailability;
  intercomAvailability?: ILiftStatsAvailability;
  maintenanceExpired?: ILiftStatsCount;
  maintenanceBeforeInterval?: ILiftStatsCount;
  maintenanceOnInterval?: ILiftStatsCount;
  maintenanceAfterInterval?: ILiftStatsCount;
  approval?: ILiftStatsCount;
}

interface ISmallCardsGridProps {
  loading?: boolean;
  liftStats: ILiftStats;
  viewIsCurrent?: boolean;
  showLiftAvailability?: boolean;
  hideDetailsNavigation?: boolean;
  liftFlowAvailable?: boolean;
  periodicalCounterData?: { items: Array<{ sum: number }> };
}

const SmallCardsGrid = ({
  loading = false,
  liftStats,
  viewIsCurrent,
  showLiftAvailability = false,
  hideDetailsNavigation = false,
}: ISmallCardsGridProps) => {
  const [globalState] = useGlobalState();

  const user = useUser();

  const numberFormat = new Intl.NumberFormat(globalState.locale, {
    maximumFractionDigits: 2,
  });

  const navigate = useNavigate();

  return (
    <div className={classes.wrapper}>
      {viewIsCurrent ? (
        <>
          {!user.isClientLight && !user.isMechanic && (
            <>
              <div className={classes.threeRow}>
                <SmallCard
                  loading={loading}
                  hideDetailsNavigation={hideDetailsNavigation}
                  data={numberFormat.format(liftStats?.liftUnavailable ?? 0)}
                  title={'Liften onbeschikbaar'}
                  tooltip={'Het aantal op dit moment onbeschikbare liften'}
                  percent={false}
                  color={
                    (liftStats?.liftUnavailable ?? 0) > 0
                      ? '#F3776D'
                      : '#86B29C'
                  }
                  showDetails={() =>
                    navigate(
                      [
                        '/stats-details/liftUnavailable',
                        statsQuerySerialiser({
                          statsType: 'actual',
                        }),
                      ].join('')
                    )
                  }
                />

                <SmallCard
                  loading={loading}
                  hideDetailsNavigation={hideDetailsNavigation}
                  data={numberFormat.format(liftStats?.senseFailureSensor ?? 0)}
                  title={'Technisch onbeschikbaar'}
                  tooltip={
                    'Het aantal op dit moment onbeschikbare liften vanwege een technische storing'
                  }
                  percent={false}
                  color={
                    (liftStats?.senseFailureSensor ?? 0) > 0
                      ? '#F3776D'
                      : '#86B29C'
                  }
                  showDetails={() =>
                    navigate(
                      [
                        '/stats-details/senseFailureSensor',
                        statsQuerySerialiser({
                          statsType: 'actual',
                        }),
                      ].join('')
                    )
                  }
                />

                <SmallCard
                  loading={loading}
                  hideDetailsNavigation={hideDetailsNavigation}
                  data={numberFormat.format(liftStats?.visits ?? 0)}
                  title={'Bezoeken'}
                  tooltip={
                    'Het aantal liften waar op dit moment een bezoek gaande is'
                  }
                  percent={false}
                  color={(liftStats?.visits ?? 0) > 0 ? '#F3776D' : '#86B29C'}
                  showDetails={() =>
                    navigate(
                      [
                        '/stats-details/visits',
                        statsQuerySerialiser({
                          statsType: 'actual',
                        }),
                      ].join('')
                    )
                  }
                />
              </div>

              <div className={classes.fourRow}>
                <SmallCard
                  loading={loading}
                  hideDetailsNavigation={hideDetailsNavigation}
                  data={numberFormat.format(
                    liftStats?.servicecounterMaintenanceAlert ?? 0
                  )}
                  title={'Liften onderhoud onvoldoende'}
                  tooltip={
                    'Het aantal liften waarop op dit moment onderhoud uitgevoerd had moeten zijn'
                  }
                  percent={false}
                  color={
                    (liftStats?.servicecounterMaintenanceAlert ?? 0) > 0
                      ? '#F3776D'
                      : '#86B29C'
                  }
                  showDetails={() =>
                    navigate(
                      [
                        '/stats-details/servicecounterMaintenanceAlert',
                        statsQuerySerialiser({
                          statsType: 'actual',
                        }),
                      ].join('')
                    )
                  }
                />

                <SmallCard
                  loading={loading}
                  hideDetailsNavigation={hideDetailsNavigation}
                  data={numberFormat.format(
                    typeof liftStats?.approvalRejected === 'number'
                      ? liftStats.approvalRejected
                      : 0
                  )}
                  title={'Liften afgekeurd'}
                  tooltip={'Het aantal liften dat op dit moment afgekeurd is'}
                  percent={false}
                  color={
                    (typeof liftStats?.approvalRejected === 'number'
                      ? liftStats.approvalRejected
                      : 0) > 0
                      ? '#F3776D'
                      : '#86B29C'
                  }
                  showDetails={() =>
                    navigate(
                      [
                        '/stats-details/approvalRejected',
                        statsQuerySerialiser({
                          statsType: 'actual',
                        }),
                      ].join('')
                    )
                  }
                />
                <SmallCard
                  loading={loading}
                  hideDetailsNavigation={hideDetailsNavigation}
                  data={numberFormat.format(
                    typeof liftStats?.approvalExpired === 'number'
                      ? liftStats.approvalExpired
                      : 0
                  )}
                  title={'Liften verlopen keuring'}
                  tooltip={
                    'Het aantal liften waarvan op dit moment de keuring verlopen is'
                  }
                  percent={false}
                  color={
                    (typeof liftStats?.approvalExpired === 'number'
                      ? liftStats.approvalExpired
                      : 0) > 0
                      ? '#F3776D'
                      : '#86B29C'
                  }
                  showDetails={() =>
                    navigate(
                      [
                        '/stats-details/approvalExpired',
                        statsQuerySerialiser({
                          statsType: 'actual',
                        }),
                      ].join('')
                    )
                  }
                />
              </div>
            </>
          )}

          <div className={classes.twoRow}>
            <SmallCard
              loading={loading}
              hideDetailsNavigation={hideDetailsNavigation}
              data={numberFormat.format(liftStats?.intercomUnavailable ?? 0)}
              title={'Spreekluister onbeschikbaar'}
              tooltip={
                'Het aantal spreekluistersystemen waarvan de periodieke test op dit moment gefaald is'
              }
              percent={false}
              color={
                (liftStats?.intercomUnavailable ?? 0) > 0
                  ? '#F3776D'
                  : '#86B29C'
              }
              showDetails={() =>
                navigate(
                  [
                    '/stats-details/intercomUnavailable',
                    statsQuerySerialiser({
                      statsType: 'actual',
                    }),
                  ].join('')
                )
              }
            />

            <SmallCard
              loading={loading}
              hideDetailsNavigation={hideDetailsNavigation}
              data={numberFormat.format(liftStats?.senseNotConnected ?? 0)}
              title={'Sense module geen verbinding'}
              tooltip={
                'Het aantal liften waarvan de Sense module niet volledig verbonden is'
              }
              percent={false}
              color={
                (liftStats?.senseNotConnected ?? 0) > 0 ? '#F3776D' : '#86B29C'
              }
              showDetails={() =>
                navigate(
                  [
                    '/stats-details/senseNotConnected',
                    statsQuerySerialiser({
                      statsType: 'actual',
                    }),
                  ].join('')
                )
              }
            />
          </div>
        </>
      ) : (
        <>
          <div className={classes.twoRow}>
            <SmallCard
              loading={loading}
              hideDetailsNavigation={hideDetailsNavigation}
              data={
                typeof liftStats?.approvalRejected === 'object'
                  ? (liftStats.approvalRejected.count ?? 0)
                  : 0
              }
              title={'Aantal afkeuringen'}
              tooltip={
                'Aantal aaneengesloten periodes van afkeuringen gedurende deze periode inclusief afkeuringen uit de vorige periode die nog liepen tijdens de periode'
              }
              color={
                (typeof liftStats?.approvalRejected === 'object'
                  ? (liftStats.approvalRejected.count ?? 0)
                  : 0) > 0
                  ? '#F3776D'
                  : undefined
              }
              showDetails={() =>
                navigate(
                  [
                    '/stats-details/approvalRejected',
                    statsQuerySerialiser({
                      statsType: 'historic',
                    }),
                  ].join('')
                )
              }
            />

            <SmallCard
              loading={loading}
              hideDetailsNavigation={hideDetailsNavigation}
              data={
                typeof liftStats?.approvalExpired === 'object'
                  ? (liftStats.approvalExpired.lift_count ?? 0)
                  : 0
              }
              title={'Verlopen liftkeuringen'}
              tooltip={'Het aantal unieke liften met een verlopen keuring'}
              color={
                (typeof liftStats?.approvalExpired === 'object'
                  ? (liftStats.approvalExpired.count ?? 0)
                  : 0) > 0
                  ? '#F3776D'
                  : undefined
              }
              showDetails={() =>
                navigate(
                  [
                    '/stats-details/approvalExpired',
                    statsQuerySerialiser({
                      statsType: 'historic',
                    }),
                  ].join('')
                )
              }
            />
            {showLiftAvailability && (
              <SmallCard
                loading={loading}
                hideDetailsNavigation={hideDetailsNavigation}
                data={numberFormat.format(
                  liftStats?.liftAvailability?.percentage ?? 0
                )}
                title={'Beschikbaarheid lift'}
                tooltip={
                  'De beschikbaarheid van de liftinstallatie in de geselecteerde periode'
                }
                percent={true}
                color={getAvailabilityColor(
                  liftStats?.liftAvailability?.percentage ?? 0
                )}
              />
            )}
          </div>

          <div className={classes.fourRow}>
            <SmallCard
              loading={loading}
              hideDetailsNavigation={hideDetailsNavigation}
              data={liftStats?.maintenanceBeforeInterval?.count ?? 0}
              title={'Onderhoudsbezoek eerder'}
              tooltip={
                'Het aantal uitgevoerde onderhoudsbezoeken wat uitgevoerd is voor het bereiken van het rittenteller gebaseerde onderhoudsinterval'
              }
              color={'#F3C46D'}
              showDetails={() =>
                navigate(
                  [
                    '/stats-details/maintenanceBeforeInterval',
                    statsQuerySerialiser({
                      statsType: 'historic',
                    }),
                  ].join('')
                )
              }
            />
            <SmallCard
              loading={loading}
              hideDetailsNavigation={hideDetailsNavigation}
              data={liftStats?.maintenanceOnInterval?.count ?? 0}
              title={'Onderhoudsbezoek op tijd'}
              tooltip={
                'Het aantal uitgevoerde onderhoudsbezoeken wat uitgevoerd is rond het rittenteller gebaseerde onderhoudsinterval'
              }
              color={'#86B29C'}
              showDetails={() =>
                navigate(
                  [
                    '/stats-details/maintenanceOnInterval',
                    statsQuerySerialiser({
                      statsType: 'historic',
                    }),
                  ].join('')
                )
              }
            />
            <SmallCard
              loading={loading}
              hideDetailsNavigation={hideDetailsNavigation}
              data={liftStats?.maintenanceAfterInterval?.count ?? 0}
              title={'Onderhoudsbezoek later'}
              tooltip={
                'Het aantal uitgevoerde onderhoudsbezoeken wat uitgevoerd is later dan bereiken van het rittenteller gebaseerde onderhoudsinterval'
              }
              color={'#F3776D'}
              showDetails={() =>
                navigate(
                  [
                    '/stats-details/maintenanceAfterInterval',
                    statsQuerySerialiser({
                      statsType: 'historic',
                    }),
                  ].join('')
                )
              }
            />
            <SmallCard
              loading={loading}
              hideDetailsNavigation={hideDetailsNavigation}
              data={liftStats?.maintenanceExpired?.lift_count ?? 0}
              title={'Interval overschreden'}
              tooltip={
                'Het aantal unieke liften waarbij het rittenteller gebaseerd onderhoudsinterval is overschreden'
              }
              color={'#F3776D'}
              showDetails={() =>
                navigate(
                  [
                    '/stats-details/maintenanceExpired',
                    statsQuerySerialiser({
                      statsType: 'historic',
                    }),
                  ].join('')
                )
              }
            />
          </div>
        </>
      )}
    </div>
  );
};

export { SmallCardsGrid };
